import { Component } from '@theme/component';
import { WishlistStore, WishlistUpdateEvent } from '@theme/wishlist-store';
import { CompareStore, CompareUpdateEvent } from '@theme/compare-store';

/**
 * A custom element for the "..." product actions menu on product cards.
 * Shows a dropdown with Wishlist and Compare options.
 *
 * @extends {Component}
 */
class ProductActionsComponent extends Component {
  /** @type {boolean} */
  #menuOpen = false;

  connectedCallback() {
    super.connectedCallback();
    this.#syncState();
    document.addEventListener(WishlistUpdateEvent.eventName, this.#syncState);
    document.addEventListener(CompareUpdateEvent.eventName, this.#syncState);
    document.addEventListener('click', this.#handleOutsideClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(WishlistUpdateEvent.eventName, this.#syncState);
    document.removeEventListener(CompareUpdateEvent.eventName, this.#syncState);
    document.removeEventListener('click', this.#handleOutsideClick);
  }

  /**
   * Toggle the dropdown menu.
   * @param {PointerEvent} event
   */
  toggleMenu(event) {
    event.preventDefault();
    event.stopPropagation();

    this.#menuOpen = !this.#menuOpen;
    const menu = this.querySelector('.product-actions__menu');
    const trigger = this.querySelector('.product-actions__trigger');

    if (menu) menu.hidden = !this.#menuOpen;
    if (trigger) trigger.setAttribute('aria-expanded', String(this.#menuOpen));
  }

  /**
   * Toggle wishlist state for this product.
   * @param {PointerEvent} event
   */
  toggleWishlist(event) {
    event.preventDefault();
    event.stopPropagation();

    WishlistStore.toggle(this.#getProductData());
  }

  /**
   * Toggle compare state for this product.
   * @param {PointerEvent} event
   */
  toggleCompare(event) {
    event.preventDefault();
    event.stopPropagation();

    const product = this.#getProductData();
    const added = CompareStore.toggle(product);

    // Show feedback if limit reached
    if (added === false && !CompareStore.has(product.handle)) {
      const label = this.querySelector('.product-actions__compare-label');
      if (label) {
        const original = label.textContent;
        label.textContent = `Max. ${CompareStore.MAX_ITEMS} Produkte`;
        setTimeout(() => { label.textContent = original; }, 1500);
      }
    }
  }

  #getProductData() {
    return {
      handle: this.dataset.productHandle,
      id: Number(this.dataset.productId),
      title: this.dataset.productTitle,
      url: this.dataset.productUrl,
      image: this.dataset.productImage,
      price: this.dataset.productPrice,
      variantId: this.dataset.variantId,
    };
  }

  #handleOutsideClick = (event) => {
    if (this.#menuOpen && !this.contains(event.target)) {
      this.#menuOpen = false;
      const menu = this.querySelector('.product-actions__menu');
      const trigger = this.querySelector('.product-actions__trigger');
      if (menu) menu.hidden = true;
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    }
  };

  #syncState = () => {
    const handle = this.dataset.productHandle;

    // Sync wishlist button state
    const wishlistBtn = this.querySelector('.product-actions__menu-item:first-child');
    if (wishlistBtn) {
      const isInWishlist = WishlistStore.has(handle);
      wishlistBtn.toggleAttribute('data-active', isInWishlist);

      const icon = wishlistBtn.querySelector('.product-actions__wishlist-icon');
      if (icon) {
        icon.setAttribute('fill', isInWishlist ? 'currentColor' : 'none');
      }
    }

    // Sync compare button state
    const compareBtn = this.querySelector('.product-actions__menu-item:last-child');
    if (compareBtn) {
      compareBtn.toggleAttribute('data-active', CompareStore.has(handle));
    }
  };
}

if (!customElements.get('product-actions-component')) {
  customElements.define('product-actions-component', ProductActionsComponent);
}
